import { useCallback, useEffect, useState } from 'react';

import { getQueryParamValue } from 'src/lib/browser';
import { getParsedGraphQLFirstError } from 'src/lib/graphql';
import * as ls from 'src/lib/localData';
import { ROUTE_PARAMS } from 'src/routes';
import { useNavigateToApplication } from 'src/services/useNavigateToApplication';
import { useUserActions } from 'src/store';

const onLoginSuccess = () => {
  // Clear "previously" selected workspace on login, so we can fallback to the "default" one
  ls.currentWorkspaceId.remove();
};

export const useViewModel = () => {
  const userActions = useUserActions();
  const navigateToApplication = useNavigateToApplication();

  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check login state on page load
  useEffect(() => {
    setLoading(true);

    userActions
      .verifyAuthentication()
      .then(() => {
        setLoading(false);
        navigateToApplication({ addFrom: true });
      })
      .catch(() => {
        setLoading(false);
      });
  }, [userActions, navigateToApplication]);

  // Run authentication
  const handleSubmit = useCallback(
    async (email, password) => {
      setSubmitting(true);
      setError(null);
      try {
        await userActions.login(email, password);

        if (getQueryParamValue(ROUTE_PARAMS.autoClose) !== undefined) {
          window.close();
          return;
        }

        onLoginSuccess();
        navigateToApplication({ addFrom: true });
      } catch (e) {
        const { message, code } = getParsedGraphQLFirstError(e) || {};

        setErrorCode(code);

        if (message.includes('Authorization failed')) {
          setError('Email or password is incorrect');
        } else {
          setError(message);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [userActions, navigateToApplication]
  );

  /**
   * Handle start of oauth process
   */
  const handleOAuthStart = useCallback(() => {
    setError(null);
  }, []);

  const handleOAuthSuccess = useCallback(
    async (result) => {
      try {
        await userActions.loginOAuth(result);

        onLoginSuccess();
        navigateToApplication({ addFrom: true, wasUserCreated: result.wasUserCreated });
      } catch (loginError) {
        const { message, code } = getParsedGraphQLFirstError(loginError) || {};

        setErrorCode(code);
        setError(message);
      }
    },
    [userActions, navigateToApplication]
  );

  const handleOAuthError = useCallback((e) => {
    setError(e ? e.message : e);
  }, []);

  return {
    loading,
    submitting,
    error,
    errorCode,
    handleSubmit,
    handleOAuthStart,
    handleOAuthSuccess,
    handleOAuthError,
  };
};
